import { LIVE_API, ENDPOINTS } from './config'

export type AnalyticsEventName =
  | 'page_view'
  | 'article_gate_view'
  | 'lead_captured'
  | 'ebook_download'
  | 'assessment_completed'
  | 'configurator_submitted'
  | 'discovery_call_booked'
  | 'portal_login'
  | 'deliverable_downloaded'
  | 'webinar_registered'
  | 'webinar_replay_unlocked'

export interface AnalyticsPayload {
  path?: string
  referrer?: string
  source?: string
  line?: string
  sector?: string
  resource?: string
  score?: number
  band?: string
  packageName?: string
  role?: string
  project?: string
  file?: string
  /** Webinar slug, for registration and replay events. */
  webinar?: string
  /** Lead lane from the webinar scoring: hot, warm or nurture. */
  lane?: string
}

export interface RecordedEvent {
  name: AnalyticsEventName
  payload: AnalyticsPayload
  at: string
}

const recorded: RecordedEvent[] = []

/**
 * Single typed event helper. Every lead capture point fires through here so the
 * KPI dashboard stays consistent.
 *
 * The server route forwards to Plausible or GA4 and mirrors the important events
 * into HubSpot for attribution. It is fired and forgotten: an analytics failure
 * must never block a lead capture or a download.
 */
export function track(name: AnalyticsEventName, payload: AnalyticsPayload = {}): void {
  const event: RecordedEvent = { name, payload, at: new Date().toISOString() }
  recorded.push(event)

  if (typeof window === 'undefined') return

  if (!LIVE_API) {
    // eslint-disable-next-line no-console
    console.info('[analytics]', name, payload)
    return
  }

  const body = JSON.stringify(event)

  // sendBeacon survives a page unload, which matters for discovery_call_booked
  // and for downloads that navigate away immediately.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' })
    if (navigator.sendBeacon(ENDPOINTS.events, blob)) return
  }

  void fetch(ENDPOINTS.events, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => {
    /* analytics must never surface an error to the visitor */
  })
}

export function getRecordedEvents(): RecordedEvent[] {
  return [...recorded]
}
